import React, { useRef } from 'react';
import FullCalendar, {
	EventClickArg,
	EventContentArg,
} from '@fullcalendar/react';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import { useAppContext } from '../../../../Services/AppContext';
import { Moment } from 'moment';
import { getMomentDate } from '../../../../Utils/dates.util';
import useTranslation from '../../../../Hooks/useTranslation';
import { CalendarView } from '../../Enums/CalendarView.eunm';

export interface CalendarEvent {
	id: string;
	start: string;
	end: string;
	title: string;
	backgroundColor?: string;
	borderColor?: string;
	textColor?: string;
	extendedProps?: Record<string, any>;
	allDay?: boolean;
}

interface Props {
	events: CalendarEvent[];
	onDateRangeChange: (start: Moment, end: Moment) => void;
	onDateClick: (start: Moment, end: Moment) => void;
	onEventClick: (data: EventClickArg) => void;
	onViewChange?: (view: CalendarView) => void;
	renderEvent?: (view: EventContentArg) => void;
}

const Calendar: React.FC<Props> = ({
	events,
	onDateRangeChange,
	onDateClick,
	onEventClick,
	renderEvent,
}) => {
	const calendarRef = useRef<FullCalendar | null>(null);
	const { language } = useAppContext();
	const { t } = useTranslation();

	//CALL ON CHANGE DATE RANGE
	const handleDatesSet = (dateInfo: { startStr: string; endStr: string }) => {
		onDateRangeChange(
			getMomentDate(dateInfo.startStr),
			getMomentDate(dateInfo.endStr),
		);
	};

	//CALL ON DATE CLICK
	const handleOnDateClick = (data: { startStr: string; endStr: string }) => {
		onDateClick(getMomentDate(data.startStr), getMomentDate(data.endStr));
	};

	//CALL ON EVENT CLICK
	const handleOnEventClick = (data: EventClickArg) => {
		onEventClick(data);
	};

	return (
		<div className='fullCalendarTimeline dynamicCalendar'>
			<FullCalendar
				ref={calendarRef}
				schedulerLicenseKey={'0784609858-fcs-1664878357'}
				plugins={[interactionPlugin, listPlugin]}
				initialView='listWeek'
				aspectRatio={1.5}
				headerToolbar={{
					left: 'prev,next,today',
					center: 'title',
					right: '',
				}}
				locale={language}
				noEventsText={t('base.noEventsToDisplay')}
				resourceGroupField='groupKey'
				events={events}
				nowIndicator={true}
				selectable={true}
				longPressDelay={100}
				selectMirror={true}
				buttonText={{
					today: t('base.shared.now'),
					month: t('base.months.one'),
					week: t('base.weeks.one'),
					day: t('base.days.one'),
					list: t('base.shared.list'),
				}}
				firstDay={1}
				eventClick={handleOnEventClick}
				datesSet={handleDatesSet}
				select={handleOnDateClick}
				eventContent={renderEvent}
				allDayText={t('component.time.wholeDay')}
			/>
		</div>
	);
};

export default Calendar;
